import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class JwtValidatorService {
  /**
   * Verifica si un token JWT ha expirado
   * @param token Token JWT a validar
   * @returns true si el token está expirado o es inválido, false si es válido
   */
  public isTokenExpired(token: string): boolean {
    try {
      const expiry = this.extractExpirationTimestamp(token);
      if (expiry === null) {
        return true;
      }
      // Buffer de 30 segundos: considerar expirado si queda menos de 30s
      const now = Math.floor(Date.now() / 1000);
      return now >= expiry - 30;
    } catch {
      return true;
    }
  }

  /**
   * Obtiene la fecha de expiración de un token JWT
   * @param token Token JWT
   * @returns Date de expiración o null si no se puede extraer
   */
  public getTokenExpiration(token: string): Date | null {
    try {
      const expiry = this.extractExpirationTimestamp(token);
      if (expiry === null) {
        return null;
      }
      return new Date(expiry * 1000);
    } catch {
      return null;
    }
  }

  /**
   * Extrae el timestamp de expiración del claim 'exp' del JWT
   * @param token Token JWT
   * @returns Timestamp de expiración o null
   */
  private extractExpirationTimestamp(token: string): number | null {
    const payload = this.decodeJwt(token);
    return payload?.exp ?? null;
  }

  /**
   * Decodifica el payload de un JWT sin verificar la firma
   * @param token Token JWT
   * @returns Payload decodificado o null si falla
   */
  private decodeJwt(token: string): any {
    try {
      if (!token || typeof token !== 'string') {
        return null;
      }

      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

      // Decodificar Base64 y manejar caracteres especiales
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }
}
